import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError, BACKEND } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CopySimple, Trash, PencilSimple } from "@phosphor-icons/react";

export default function Media() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ alt_text: "", caption: "" });

  const load = async () => {
    const params = filter !== "all" ? { kind: filter } : {};
    const { data } = await api.get("/admin/media", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/admin/media/upload?kind=any", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const copy = (url) => {
    navigator.clipboard.writeText(`${BACKEND}${url}`);
    toast.success("URL copied");
  };
  const remove = async (id) => {
    if (!confirm("Delete file?")) return;
    try { await api.delete(`/admin/media/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (it) => { setEditing(it); setEditForm({ alt_text: it.alt_text || "", caption: it.caption || "" }); };
  const saveEdit = async () => {
    try { await api.put(`/admin/media/${editing.id}`, editForm); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Media Library"
        subtitle="All uploaded files. Use this to reuse assets across content."
        actions={
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm cursor-pointer transition-colors duration-200">
            <input type="file" className="hidden" onChange={upload} data-testid="media-upload" />
            Upload file
          </label>
        }
      />
      <div className="p-8 space-y-4">
        <div className="max-w-xs">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="media-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="doc">Documents</SelectItem>
              <SelectItem value="video">Video</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="media-grid">
          {items.length === 0 && <div className="col-span-full text-center py-12 text-[#4B5563]">Empty library.</div>}
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden group" data-testid={`media-${it.id}`}>
              <div className="aspect-square bg-[#F7F8F6] flex items-center justify-center overflow-hidden">
                {it.kind === "image" ? (
                  <img src={`${BACKEND}${it.url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-[#4B5563] uppercase tracking-[0.18em]">{it.kind}</span>
                )}
              </div>
              <div className="p-2">
                <div className="text-xs text-[#1F2937] truncate" title={it.original_name}>{it.original_name}</div>
                <div className="text-[10px] text-[#4B5563]">{Math.round((it.size || 0) / 1024)} KB · {it.alt_text ? "Alt set" : "No alt"}</div>
                <div className="mt-2 flex items-center justify-between">
                  <button onClick={() => copy(it.url)} className="text-[#4B5563] hover:text-[#C2410C]" data-testid={`copy-${it.id}`}><CopySimple size={14} /></button>
                  <button onClick={() => openEdit(it)} className="text-[#4B5563] hover:text-[#C2410C]" data-testid={`edit-${it.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(it.id)} className="text-[#4B5563] hover:text-[#EF4444]" data-testid={`del-${it.id}`}><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="media-edit-dialog">
          <DialogHeader><DialogTitle>Edit file metadata</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Alt text (for screen readers + image SEO)</Label>
              <Input value={editForm.alt_text} onChange={(e) => setEditForm({ ...editForm, alt_text: e.target.value })} placeholder="Describe what the image shows" data-testid="alt-text" />
            </div>
            <div className="space-y-2">
              <Label>Caption (optional)</Label>
              <Input value={editForm.caption} onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })} data-testid="caption" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-media-meta" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
